import { httpsCallable } from "firebase/functions";
import type { HttpsCallable } from "firebase/functions";

import type { PlayoffBestOf } from "../../shared/domain/playoffSeries";
import type {
  BeerDuty,
  GameAction,
  PregameAction,
  ScheduleField,
  ScheduleTimeSlot,
} from "../shared/types/contracts";
import { functions } from "../firebase/client";
import type { MigrationSeed } from "./migration";

export interface TeamRequest {
  teamId: string;
}

export interface TeamActionRequest extends TeamRequest {
  actionId: string;
}

export interface RevisionRequest extends TeamActionRequest {
  expectedRevision: number;
}

export interface BootstrapTeamRequest {
  secret: string;
  teamName?: string;
  seed?: MigrationSeed;
}

export interface BootstrapTeamResponse {
  teamId: string;
  inviteToken: string;
  inviteVersion: number;
}

export interface RedeemInviteRequest extends TeamRequest {
  token: string;
}

export interface RedeemInviteResponse {
  teamId: string;
  inviteVersion: number;
  alreadyMember: boolean;
}

export interface InviteMutationResponse {
  inviteVersion: number | null;
  replayed: boolean;
}

export interface RotateInviteResponse extends InviteMutationResponse {
  inviteToken: string | null;
}

export interface ClaimPlayerRequest extends TeamActionRequest {
  playerId: string;
  replaceExistingClaim?: boolean;
  expectedPreviousUid?: string;
}

export interface ClaimPlayerResponse {
  claimedPlayerId: string;
  reclaimed: boolean;
  replayed: boolean;
}

export interface ClearPlayerClaimRequest extends TeamActionRequest {
  playerId: string;
}

export interface ClearPlayerClaimResponse {
  clearedPlayerId: string;
  replayed: boolean;
}

export interface SwitchPlayerClaimRequest extends TeamActionRequest {
  toPlayerId: string;
}

export interface SwitchPlayerClaimResponse {
  claimedPlayerId: string;
  replayed: boolean;
}

export interface RemoveMemberRequest extends TeamActionRequest {
  uid: string;
}

export interface RemoveMemberResponse {
  removedUid: string;
  replayed: boolean;
}

export interface MergeMemberIdentityResponse {
  claimedPlayerId: string | null;
  authProviders: string[];
}

export interface ApplyPregameActionRequest extends TeamActionRequest {
  expectedRevision?: number;
  action: PregameAction;
}

export interface RevisionResponse {
  committedRevision: number;
  replayed: boolean;
}

export interface StartGameResponse extends RevisionResponse {
  gameId: string | null;
}

export interface ScheduleInput {
  scheduledDate: string;
  timeSlot: ScheduleTimeSlot;
  field: ScheduleField;
  opponentName: string;
  isHome: boolean;
  beerDuty: BeerDuty;
}

export type SchedulePatch = Partial<ScheduleInput>;

export interface CreateScheduleRequest extends TeamActionRequest {
  schedule: ScheduleInput;
}

export interface CreateScheduleResponse {
  scheduleId: string | null;
  revision?: number;
  replayed: boolean;
}

export interface ScheduleMutationRequest extends RevisionRequest {
  scheduleId: string;
}

export interface UpdateScheduleRequest extends ScheduleMutationRequest {
  patch: SchedulePatch;
}

export interface SelectScheduleRequest extends TeamActionRequest {
  scheduleId: string;
}

export interface PlayoffSeriesInput {
  title: string;
  opponentName: string;
  bestOf: PlayoffBestOf;
  games: Array<Omit<ScheduleInput, "opponentName">>;
}

export interface CreatePlayoffSeriesRequest extends TeamActionRequest {
  series: PlayoffSeriesInput;
}

export interface CreatePlayoffSeriesResponse {
  seriesId: string | null;
  scheduleIds: string[];
  revision?: number;
  replayed: boolean;
}

export interface PlayoffSeriesMutationRequest extends RevisionRequest {
  seriesId: string;
}

export interface UpdatePlayoffSeriesRequest extends PlayoffSeriesMutationRequest {
  patch: Partial<Pick<PlayoffSeriesInput, "title" | "opponentName">>;
}

export interface ApplyGameActionRequest extends RevisionRequest {
  gameId: string;
  action: GameAction;
}

export interface GameMutationRequest extends RevisionRequest {
  gameId: string;
}

export interface FinalizeGameRequest extends GameMutationRequest {
  forfeitWinner?: "us" | "them";
}

export interface UndoGameResponse extends RevisionResponse {
  undoneEventId: string | null;
}

export interface ClaimScoringRequest extends TeamActionRequest {
  gameId: string;
}

export interface ClaimScoringResponse {
  scorerUid: string;
  scorerNameSnapshot: string | null;
  replayed?: boolean;
  alreadyScorer?: boolean;
}

export interface StartSeasonRequest extends TeamActionRequest {
  year: number;
}

export interface StartSeasonResponse {
  seasonId: string;
  revision?: number;
  replayed: boolean;
}

export interface SeasonMutationRequest extends TeamActionRequest {
  seasonId: string;
}

const call = <Request, Response>(
  name: string,
): HttpsCallable<Request, Response> =>
  httpsCallable<Request, Response>(functions, name);

// One actionId per user INTENT — retries of the same intent reuse the id (server idempotency).
export const newActionId = (): string => crypto.randomUUID();

export const bootstrapTeam = call<BootstrapTeamRequest, BootstrapTeamResponse>(
  "bootstrapTeam",
);
export const redeemInvite = call<RedeemInviteRequest, RedeemInviteResponse>(
  "redeemInvite",
);
export const rotateInvite = call<TeamActionRequest, RotateInviteResponse>(
  "rotateInvite",
);
export const revokeInvite = call<TeamActionRequest, InviteMutationResponse>(
  "revokeInvite",
);
export const claimPlayer = call<ClaimPlayerRequest, ClaimPlayerResponse>(
  "claimPlayer",
);
export const clearPlayerClaim = call<
  ClearPlayerClaimRequest,
  ClearPlayerClaimResponse
>("clearPlayerClaim");
export const switchPlayerClaim = call<
  SwitchPlayerClaimRequest,
  SwitchPlayerClaimResponse
>("switchPlayerClaim");
export const removeMember = call<RemoveMemberRequest, RemoveMemberResponse>(
  "removeMember",
);
export const mergeMemberIdentity = call<
  TeamRequest,
  MergeMemberIdentityResponse
>("mergeMemberIdentity");
export const applyPregameAction = call<
  ApplyPregameActionRequest,
  RevisionResponse
>("applyPregameAction");
export const startGame = call<RevisionRequest, StartGameResponse>("startGame");
export const createScheduleGame = call<
  CreateScheduleRequest,
  CreateScheduleResponse
>("createScheduleGame");
export const updateScheduleGame = call<UpdateScheduleRequest, RevisionResponse>(
  "updateScheduleGame",
);
export const selectScheduleGame = call<SelectScheduleRequest, RevisionResponse>(
  "selectScheduleGame",
);
export const deleteScheduleGame = call<
  ScheduleMutationRequest,
  RevisionResponse
>("deleteScheduleGame");
export const createPlayoffSeries = call<
  CreatePlayoffSeriesRequest,
  CreatePlayoffSeriesResponse
>("createPlayoffSeries");
export const updatePlayoffSeries = call<
  UpdatePlayoffSeriesRequest,
  RevisionResponse
>("updatePlayoffSeries");
export const deletePlayoffSeries = call<
  PlayoffSeriesMutationRequest,
  RevisionResponse
>("deletePlayoffSeries");
export const applyGameAction = call<ApplyGameActionRequest, RevisionResponse>(
  "applyGameAction",
);
export const undoGameAction = call<GameMutationRequest, UndoGameResponse>(
  "undoGameAction",
);
export const finalizeGame = call<FinalizeGameRequest, RevisionResponse>(
  "finalizeGame",
);
export const archiveGame = call<GameMutationRequest, RevisionResponse>(
  "archiveGame",
);
export const discardGame = call<GameMutationRequest, RevisionResponse>(
  "discardGame",
);
export const claimScoring = call<ClaimScoringRequest, ClaimScoringResponse>(
  "claimScoring",
);
export const startSeason = call<StartSeasonRequest, StartSeasonResponse>(
  "startSeason",
);
export const closeSeason = call<SeasonMutationRequest, RevisionResponse>(
  "closeSeason",
);
export const reopenSeason = call<SeasonMutationRequest, RevisionResponse>(
  "reopenSeason",
);

// Latency probe: logs "[perf] <label>: <ms>" for the wrapped promise (console.debug).
export const timed = <T>(label: string, promise: Promise<T>): Promise<T> => {
  const startedAt = performance.now();
  return promise.finally(() =>
    console.debug(
      `[perf] ${label}: ${Math.round(performance.now() - startedAt)}ms`,
    ),
  );
};

interface CallableErrorLike {
  code?: unknown;
  details?: unknown;
  message?: unknown;
}

// Human-readable message for a callable HttpsError (client SDK prefixes code with "functions/").
export function friendlyError(error: unknown): string {
  const candidate: CallableErrorLike =
    typeof error === "object" && error !== null ? error : {};
  const code =
    typeof candidate.code === "string"
      ? candidate.code.replace(/^functions\//, "")
      : "";
  const details =
    typeof candidate.details === "object" && candidate.details !== null
      ? (candidate.details as Record<string, unknown>)
      : {};
  const reason = details.reason;
  const message =
    typeof candidate.message === "string"
      ? candidate.message
      : "Something went wrong. Try again.";
  switch (code) {
    case "permission-denied":
      if (reason === "invalid-invite") {
        return "This invite link is invalid or has been revoked. Ask a teammate for a fresh link.";
      }
      if (reason === "already-bootstrapped") {
        return "A team already exists. Ask a teammate for an invite link instead.";
      }
      return "You don't have permission to do that.";
    case "not-found":
      return "That team doesn't exist. Check the link and try again.";
    case "failed-precondition":
      if (reason === "player-claimed") {
        return "That player was just claimed by someone else. Pick another.";
      }
      if (reason === "stale-revision") {
        return "Someone else made a change first — try again.";
      }
      if (reason === "not-scorer") {
        const scorerName = details.scorerNameSnapshot;
        return typeof scorerName === "string" && scorerName
          ? `${scorerName} is scoring — take over to make changes`
          : "Someone else is scoring — take over to make changes";
      }
      return typeof candidate.message === "string"
        ? candidate.message
        : "That action isn't allowed right now.";
    case "unauthenticated":
      return "Your session expired. Reload the page.";
    case "internal":
      return "Something went wrong. Try again.";
    default:
      return message;
  }
}
